import { useState, useContext } from "react";

import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";

import { AuthContext } from "lib/context/auth";

// UI & Components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import InputGroup from "react-bootstrap/InputGroup";
import { Typeahead } from "react-bootstrap-typeahead";

import { BsCheckCircleFill } from "react-icons/bs";

import { CREATE_USER } from "modules/user-list/gql";
import { InputField, Button } from "@evos/components";
import Header from "components/Header";
import userRoles from "lib/constants/userRoles.json";

// Apollo & Forms
import type { FieldValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { UserConnection } from "types";

function useAuth() {
  return useContext(AuthContext);
}

export default function AddUser() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const navigate = useNavigate();

  const zodAddUser = z.object({
    email: z
      .string()
      .email()
      .min(1),
    roles: z.array(z.string()).optional(),
  });

  type AddUserArguments = z.infer<typeof zodAddUser>;

  const [ userAdded, setUserAdded ] = useState("");

  const [addUser, {
    error: addUserError,
  }] = useMutation<{create_platform_user: UserConnection["users"]}, AddUserArguments>(CREATE_USER, {
    notifyOnNetworkStatusChange: true,
  });

  const {
    register,
    handleSubmit,
    getFieldState,
    control,
    formState: {
      errors,
      isSubmitted,
      isSubmitting,
    } } = useForm<AddUserArguments>({
      mode: "onSubmit",
      defaultValues: {
        email: "",
        roles: [],
      },
      resolver: zodResolver(zodAddUser),
    });

  const onSubmit = async(data: FieldValues) => {
    await addUser({
      variables: {
        email: data.email,
        roles: data.roles,
      }
    });
    setUserAdded(data.email);
  };

  return (
    <div className="AddUser">
      <Header />
      <Container fluid>
        <Tab.Container defaultActiveKey="single">
          <Row className="d-flex justify-content-center pt-5">
            <Col xs={12} md={9} four-xl={7}>
              <Card className={`mb-4 pb-3 text-start ${darkMode ? "bg-dark" : ""}`} style={{overflow: "hidden"}}>
                <Row className="d-flex justify-content-center">
                  <Col xs={12} md={3} className="responsive-border-form-column ps-4 pt-2 pb-4 pb-md-0" style={{backgroundColor: darkMode ? "#1f2226" : "#FCFCFF"}}>
                    <Nav variant="pills" className="pt-4 nav-form-item">
                      <Nav.Item className="w-100">
                        <Nav.Link eventKey="single" className={`mb-2 py-2 fw-bold ${darkMode ? "text-light" : "text-secondary"}`}>Add New User</Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Col>
                  <Col xs={12} md={9} className="pt-4">
                    <Tab.Content>
                      <Tab.Pane eventKey="single">
                        <h4 className="ps-3 pt-3 pb-4">Add New EVOS User</h4>
                        { !userAdded
                          ? <Form
                            onSubmit={handleSubmit(onSubmit)} className="ps-3 pe-4 pb-5"
                          >

                            <InputField
                              name="email"
                              label="Email"
                              required
                              register={register}
                              isSubmitted={isSubmitted}
                              getFieldState={getFieldState}
                              error={errors?.email}
                            />

                            <Form.Group className="mb-4">
                              <Form.Label>Roles</Form.Label>
                              <InputGroup className="mb-3">
                                <Controller
                                  control={control}
                                  name="roles"
                                  render={({
                                    field: { 
                                      onChange, 
                                      onBlur, 
                                      value
                                    },
                                  }) => (
                                    <Typeahead
                                      id="userRoles"
                                      multiple
                                      maxHeight="140px"
                                      options={userRoles as string[]}
                                      isValid={isSubmitted === true && getFieldState("roles")?.invalid === false}
                                      maxResults={10}
                                      allowNew={false}
                                      placeholder="Choose roles..."
                                      onChange={onChange}
                                      onBlur={onBlur}
                                      selected={value}
                                    />
                                  )}
                                />
                              </InputGroup>
                            </Form.Group>

                            <span className="d-flex align-items-center pt-3">
                              <Button 
                                variant="primary" 
                                type="submit" 
                                className="px-4" 
                                style={{borderRadius: "10px"}} 
                                isLoading={isSubmitting}
                              >
                                <p className="mb-0 px-2 fw-bold">
                                  Submit
                                </p>
                              </Button>
                              <p className="mb-0 ps-4 text-primary" style={{textDecoration: "underline", cursor: "pointer"}} onClick={() => navigate("/user-list")}>Cancel</p>
                            </span>
                            {(addUserError != null) &&
                              <p className="text-danger pt-2 mb-0">An error occurred while adding the user. Please try again.</p>
                            }
                          </Form>
                          : <Row className="d-flex justify-content-center align-items-center py-5">
                            <Col xs={12} className="d-flex justify-content-center">
                              <BsCheckCircleFill color="#149459" size={60}/>
                            </Col>
                            <Col xs={12} className="d-flex justify-content-center">
                              <h2 className="fw-bold fs-4 mt-1 mb-2 ps-1" style={{color: "#149459"}}>Done!</h2>
                            </Col>
                            <Col xs={12} className="d-flex justify-content-center">
                              <h3 className="fs-2 my-1">User added successfully.</h3>
                            </Col>
                            <Col xs={12} className="d-flex justify-content-center">
                              <p className="text-muted">User: {userAdded}</p>
                            </Col>
                            <Col xs={12} className="d-flex justify-content-center">
                              <Button variant="primary" className="px-4 mt-2" style={{borderRadius: "10px"}} onClick={() => navigate("/user-list")}>
                                <p className="mb-0 px-2 fw-bold">Back to Users</p>
                              </Button>
                            </Col>
                          </Row>
                        }
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </div>
  );
}